/**
 * Thin API client. The only identity logic on the client is what it sends:
 * the development header below in the local preview, or the Auth0 access
 * token in the hosted team demo. Everything about who may see what is
 * decided on the server.
 */
// Namespaced so other local Vite apps on the same origin cannot pollute it.
export const DEV_USER_STORAGE_KEY = 'tropicana-registry:devUserId';
const DEV_USER_HEADER = 'x-dev-user-id';

export type Role = 'distributor' | 'support_admin' | 'system_admin';

export interface Identity {
  id: number;
  displayName: string;
  role: Role;
  status: 'active' | 'inactive';
}

export interface RouteSummary {
  id: number;
  routeNumber: string;
  stopCount: number;
  cities?: string[];
  states?: string[];
  mapPoints?: { latitude: number; longitude: number }[];
}

export interface DistributorWithRoutes {
  id: number;
  name: string;
  routes: RouteSummary[];
}

export interface Stop {
  id: number;
  name: string;
  streetAddress: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  contactName: string | null;
  primaryPhone: string | null;
  secondaryPhone: string | null;
  latitude?: number | null;
  longitude?: number | null;
  googlePlaceId?: string;
}

export interface RouteDetails {
  distributorId: number;
  distributorName: string;
  routeNumber: string;
  stops: Stop[];
}

export interface CoverageResult {
  ownMatches: {
    distributorId: number;
    distributorName: string;
    routeNumber: string;
    stop: Stop;
  }[];
  servedElsewhere: boolean;
}

/* ---------- Request New Stop ---------- */

export interface RequestedCustomer {
  name: string;
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
  contactName: string | null;
  primaryPhone: string | null;
  secondaryPhone: string | null;
  googlePlaceId?: string;
}

export interface NewStopInput {
  distributorId: number;
  routeId: number;
  name: string;
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
  contactName: string;
  primaryPhone: string;
  secondaryPhone: string;
  googlePlaceId?: string;
}

export type ValidatedAddress = Pick<
  RequestedCustomer,
  'streetAddress' | 'city' | 'state' | 'zipCode' | 'googlePlaceId'
>;
export interface AddressReview {
  action: 'confirm' | 'fix' | 'add_unit';
  message: string;
  suggestedAddress?: ValidatedAddress;
}
export type AddressValidationResult =
  { action: 'accept'; address: ValidatedAddress } | AddressReview;

export interface SimilarAddressMatch {
  stopId: number;
  customerName: string;
  streetAddress: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  distributorId: number;
  distributorName: string;
  routeNumber: string;
  reasons: string[];
  comparedWith: 'submitted' | 'google';
}
export interface AddressReviewResponse {
  result: AddressValidationResult | null;
  validationError: string | null;
  similarAddresses: { matches: SimilarAddressMatch[]; total: number } | null;
  similarAddressesError: string | null;
}

export type RepRequestStatus = 'pending' | 'in_review' | 'approved' | 'rejected';

/** A rep's own request as the server shows it: no match, no reviewer, feedback only on rejection. */
export interface RepRequest {
  id: number;
  status: RepRequestStatus;
  distributorId: number;
  distributorName: string;
  routeId: number;
  routeNumber: string;
  customer: RequestedCustomer;
  submittedAt: string;
  decidedAt: string | null;
  feedback: string | null;
}

export interface MatchedRecord {
  distributorId: number;
  distributorName: string;
  routeId: number;
  routeNumber: string;
  stop: Stop;
}

/** The full record, for Xtreme support only. */
export interface SupportRequest {
  id: number;
  type: 'new_stop' | 'conflict';
  status: 'pending' | 'in_review' | 'flagged' | 'approved' | 'rejected';
  requesterId: number;
  requesterName: string;
  distributorId: number;
  distributorName: string;
  routeId: number;
  routeNumber: string;
  customer: RequestedCustomer;
  submittedAt: string;
  match: MatchedRecord | null;
  flaggedAt: string | null;
  flaggedByName: string | null;
  decidedAt: string | null;
  decidedByName: string | null;
  decision: 'approved' | 'rejected' | null;
  feedback: string | null;
  createdStopId: number | null;
  differences: { route: boolean; phone: boolean } | null;
}

export type CheckOutcome =
  | {
      outcome: 'clear';
      customer: RequestedCustomer;
      distributor: { id: number; name: string };
      route: { id: number; routeNumber: string };
    }
  | { outcome: 'conflict'; request: RepRequest };

export interface SubmitOutcome {
  outcome: 'submitted' | 'conflict';
  request: RepRequest;
}

/**
 * The type and status the reviewer's screen showed when a decision button was
 * pressed. The server refuses the decision (409) when the request has changed
 * since, so nobody decides on details they did not see.
 */
export type SeenState = Pick<SupportRequest, 'type' | 'status'>;

const seenOf = (request: SeenState): SeenState => ({ type: request.type, status: request.status });

export interface ApproveOutcome {
  outcome: 'approved' | 'returned_to_review';
  request: SupportRequest;
}

export const distributorPath = (id: number) => `/distributors/${id}`;
export const routePath = (id: number, routeNumber: string) =>
  `${distributorPath(id)}/routes/${encodeURIComponent(routeNumber)}`;

/** Public Auth0 identifiers the hosted demo's server hands to the client. Not secrets. */
export interface Auth0Settings {
  mode: 'auth0';
  domain: string;
  clientId: string;
  audience: string;
}

/**
 * How people sign in: the local preview selector ('dev'), Auth0 (the hosted
 * team demo), nothing ('none'), or a hosted server whose configuration is
 * incomplete ('unavailable').
 */
export type AuthSettings = { mode: 'dev' | 'none' | 'unavailable' | 'password' } | Auth0Settings;

export interface Health {
  status: 'ok' | 'degraded';
  dataSource: 'sql' | 'memory';
  database: 'ok' | 'unavailable';
  devIdentity: boolean;
  previewDataset?: 'imported' | 'fictional';
  auth?: AuthSettings;
}

/** The server's Auth0 settings, or null unless every value is present and well formed. */
export function auth0SettingsOf(health: Health): Auth0Settings | null {
  const auth = health.auth;
  if (!auth || auth.mode !== 'auth0') return null;
  const filled = (value: unknown): value is string => typeof value === 'string' && value !== '';
  return filled(auth.domain) && filled(auth.clientId) && filled(auth.audience) ? auth : null;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** The parsed JSON body, when the server sent one. */
    readonly body: unknown = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function isHealth(value: unknown): value is Health {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    (v.status === 'ok' || v.status === 'degraded') &&
    (v.dataSource === 'sql' || v.dataSource === 'memory') &&
    (v.database === 'ok' || v.database === 'unavailable') &&
    typeof v.devIdentity === 'boolean'
  );
}

export function readSelectedUserId(): number | null {
  try {
    const raw = window.localStorage.getItem(DEV_USER_STORAGE_KEY);
    return raw && /^\d+$/.test(raw) ? Number(raw) : null;
  } catch {
    return null;
  }
}

export function writeSelectedUserId(id: number | null): void {
  try {
    if (id === null) window.localStorage.removeItem(DEV_USER_STORAGE_KEY);
    else window.localStorage.setItem(DEV_USER_STORAGE_KEY, String(id));
  } catch {
    // Storage may be unavailable; the selection then lasts for this page only.
  }
}

export const SESSION_ENDED = 'Your session has ended. Sign in again to continue.';

/**
 * Hosted team demo only: supplies the signed-in person's Auth0 access token.
 * It is registered by HostedAuth once Auth0 reports a session and removed on
 * sign-out. The token lives in the Auth0 SDK; this module never stores it.
 */
type AccessTokenSource = () => Promise<string>;
let accessTokenSource: AccessTokenSource | null = null;

export function setAccessTokenSource(source: AccessTokenSource | null): void {
  accessTokenSource = source;
}

export function hasAccessTokenSource(): boolean {
  return accessTokenSource !== null;
}

async function apiRequest<T>(
  path: string,
  userId: number | null,
  options: RequestInit = {},
  /** The health check is the one request sent without credentials. */
  anonymous = false,
): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (accessTokenSource && !anonymous) {
    headers.Authorization = `Bearer ${await accessTokenSource()}`;
  } else if (userId !== null) {
    headers[DEV_USER_HEADER] = String(userId);
  }
  if (options.body) headers['Content-Type'] = 'application/json';
  const response = await fetch(path, { ...options, headers });
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // Non-JSON body; handled below.
  }
  if (!response.ok) {
    const message =
      body && typeof body === 'object' && 'error' in body && typeof body.error === 'string'
        ? body.error
        : `Request failed (${response.status}).`;
    throw new ApiError(response.status, message, body);
  }
  return body as T;
}

const post = (body?: unknown): RequestInit => ({
  method: 'POST',
  body: body === undefined ? undefined : JSON.stringify(body),
});

export type GoogleMapsSettings =
  { enabled: false } | { enabled: true; browserKey: string; mapId: string };

export interface ControlSettings {
  tropicanaContactName: string;
  tropicanaEmail: string;
  supportEmail: string;
  registryUrl: string;
  revision: number;
  updatedAt: string | null;
  updatedBy: { id: number; displayName: string } | null;
}

export const api = {
  controlSettings: (userId: number | null) =>
    apiRequest<{ settings: ControlSettings }>('/api/admin/settings', userId),
  saveControlSettings: (userId: number | null, settings: ControlSettings) =>
    apiRequest<{ settings: ControlSettings }>('/api/admin/settings', userId, post(settings)),
  mapsSettings: (userId: number | null) =>
    apiRequest<GoogleMapsSettings>('/api/maps/config', userId),
  demoSession: () =>
    apiRequest<{ signedIn: boolean; username: string | null }>('/api/auth/session', null, {}, true),
  demoLogin: (username: string, password: string) =>
    apiRequest<{ signedIn: boolean; username: string }>(
      '/api/auth/login',
      null,
      post({ username, password }),
      true,
    ),
  demoLogout: () =>
    apiRequest<{ signedIn: boolean; username: null }>('/api/auth/logout', null, post(), true),
  health: () =>
    apiRequest<Health>('/api/health', null, {}, true).catch((error: unknown) => {
      // A degraded (503) health response is still a valid answer. Use the
      // server's own values; only invent them when the body is unusable.
      if (error instanceof ApiError && error.status === 503) {
        if (isHealth(error.body)) return error.body;
        return {
          status: 'degraded',
          dataSource: 'sql',
          database: 'unavailable',
          devIdentity: false,
        } as Health;
      }
      throw error;
    }),
  identities: () => apiRequest<{ identities: Identity[] }>('/api/dev/identities', null),
  me: (userId: number | null) => apiRequest<Identity>('/api/me', userId),
  myDistributors: (userId: number | null) =>
    apiRequest<{ distributors: DistributorWithRoutes[] }>('/api/me/distributors', userId),
  routeDetails: (userId: number | null, distributorId: number, routeNumber: string) =>
    apiRequest<{ route: RouteDetails }>(`/api${routePath(distributorId, routeNumber)}`, userId),
  customerCoverage: (
    userId: number | null,
    name: string,
    streetAddress: string,
    location: string,
  ) =>
    apiRequest<CoverageResult>(
      '/api/customer-coverage',
      userId,
      post({ name, streetAddress, location }),
    ),

  /** First submission: the server checks the whole registry and may save a conflict. */
  checkNewStop: (userId: number | null, input: NewStopInput) =>
    apiRequest<CheckOutcome>('/api/requests/new-stop/check', userId, post(input)),
  /** Confirmed submission; the server checks again before saving. */
  submitNewStop: (userId: number | null, input: NewStopInput) =>
    apiRequest<SubmitOutcome>('/api/requests/new-stop', userId, post(input)),
  myRequests: (userId: number | null) =>
    apiRequest<{ requests: RepRequest[] }>('/api/requests/mine', userId),
  requestQueue: (userId: number | null, view: 'open' | 'history') =>
    apiRequest<{ view: string; requests: SupportRequest[] }>(`/api/requests?view=${view}`, userId),
  validateRequestAddress: (userId: number | null, id: number) =>
    apiRequest<AddressReviewResponse>(`/api/requests/${id}/validate-address`, userId, post({})),
  flagRequest: (userId: number | null, id: number, seen: SeenState) =>
    apiRequest<{ request: SupportRequest }>(
      `/api/requests/${id}/flag`,
      userId,
      post({ seen: seenOf(seen) }),
    ),
  /** A 409 carrying "returned_to_review" is an outcome, not a failure. */
  approveRequest: (userId: number | null, id: number, seen: SeenState) =>
    apiRequest<ApproveOutcome>(
      `/api/requests/${id}/approve`,
      userId,
      post({ seen: seenOf(seen) }),
    ).catch((error: unknown) => {
      if (
        error instanceof ApiError &&
        error.status === 409 &&
        error.body &&
        typeof error.body === 'object' &&
        (error.body as { outcome?: unknown }).outcome === 'returned_to_review'
      ) {
        return error.body as ApproveOutcome;
      }
      throw error;
    }),
  rejectRequest: (userId: number | null, id: number, feedback: string, seen: SeenState) =>
    apiRequest<{ request: SupportRequest }>(
      `/api/requests/${id}/reject`,
      userId,
      post({ feedback, seen: seenOf(seen) }),
    ),
};
